import React, { useEffect } from "react";
import {
  Drawer,
  Form,
  Input,
  Button,
  InputNumber,
  DatePicker,
  Select,
} from "antd";
import dayjs from "dayjs";
import { useDispatch, useSelector } from "react-redux";
import {
  createInvestmentThunk,
  updateInvestmentThunk,
  getInvestmentsThunk,
} from "../../../../Store/Slices/Finance/investments/investment.thunk";
import { fetchEntity } from "../../../../Store/Slices/Finance/entitie/entity.thunk";
import { FiTrendingUp } from "react-icons/fi";

const { Option } = Select;

const labelCls = "text-xs font-bold uppercase tracking-widest text-gray-400";
const valueCls = "text-sm font-semibold text-gray-800";

const InvestmentDetailsDrawer = ({
  open,
  onClose,
  investment = null,
  mode = "add",
}) => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();

  const isViewMode = mode === "view";

  const { entities = [], loading } = useSelector((store) => store.admin.entity);

  const bankEntities = entities?.filter((item) => item.entityType == "Share Holder");


  useEffect(() => {
    dispatch(fetchEntity({ entityType: "Bank" }));
  }, [dispatch, open]);

  useEffect(() => {
    if (!open) return;

    if (mode === "add") {
      form.resetFields();
      return;
    }

    if (investment) {
      form.setFieldsValue({
        entityId: investment?.entityId?._id || investment?.entityId,
        title: investment.title,
        description: investment.description,
        amount: investment.transactions?.[0]?.amount,
        percentage: investment.transactions?.[0]?.percentage,
        date: investment.transactions?.[0]?.date
          ? dayjs(investment.transactions[0].date)
          : null,
      });
    }
  }, [investment, mode, open, form]);

  const onFinish = async (values) => {
    const payload = {
      entityId: values.entityId,
      title: values.title,
      description: values.description,
      transactions: [
        {
          amount: values.amount,
          percentage: values.percentage,
          date: values.date.toISOString(),
        },
      ],
    };

    const action =
      mode === "edit"
        ? updateInvestmentThunk({ id: investment._id, ...payload })
        : createInvestmentThunk(payload);

    const result = await dispatch(action);

    if (!result.error) {
      dispatch(getInvestmentsThunk());
      form.resetFields();
      onClose();
    }
  };

  return (
    <Drawer
      title={
        <span className="staff-heading text-lg">
          {mode === "add"
            ? "Add Investment"
            : mode === "view"
              ? "Investment Details"
              : "Edit Investment"}
        </span>
      }
      open={open}
      onClose={() => {
        form.resetFields();
        onClose();
      }}
      width="50%"
      destroyOnClose
      bodyStyle={{ background: "#f9fafb", padding: "20px" }}
    >
      {isViewMode ? (
        /* ── View Mode ──────────────────────────── */
        <div className="flex flex-col gap-4">
          {/* Header card */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-purple-50 flex items-center justify-center flex-shrink-0">
              <FiTrendingUp className="w-6 h-6 text-purple-500" />
            </div>
            <div>
              <h3 className="text-base font-bold text-gray-900 capitalize">
                {investment?.title || "—"}
              </h3>
              <p className="text-xs text-gray-400 mt-0.5">
                {investment?.entityId?.entityName || "—"}
              </p>
            </div>
          </div>

          {/* Details card */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
            <p className={`${labelCls} mb-4`}>Investment Details</p>
            <div className="grid grid-cols-2 gap-x-8 gap-y-4">
              {[
                {
                  label: "Amount",
                  value: Number(
                    investment?.transactions?.[0]?.amount || 0,
                  ).toLocaleString(),
                },
                {
                  label: "Percentage",
                  value: investment?.transactions?.[0]?.percentage
                    ? `${investment.transactions[0].percentage}%`
                    : "—",
                },
                {
                  label: "Date",
                  value: investment?.transactions?.[0]?.date
                    ? dayjs(investment.transactions[0].date).format(
                        "DD MMM YYYY",
                      )
                    : "—",
                },
              ].map((row) => (
                <div key={row.label}>
                  <p className={labelCls}>{row.label}</p>
                  <p className={`${valueCls} mt-0.5`}>{row.value}</p>
                </div>
              ))}
            </div>
          </div>

          {investment?.description && (
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
              <p className={`${labelCls} mb-2`}>Description</p>
              <p className="text-sm text-gray-600 leading-relaxed">
                {investment.description}
              </p>
            </div>
          )}
        </div>
      ) : (
        /* ── Add / Edit Mode ────────────────────── */
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
          <p className={`${labelCls} mb-4`}>
            {mode === "edit" ? "Edit Investment" : "New Investment"}
          </p>

          <Form
            layout="vertical"
            form={form}
            onFinish={onFinish}
            disabled={isViewMode}
          >
            <Form.Item
              name="entityId"
              label={<span className={labelCls}>Select Entity</span>}
              rules={[{ required: true, message: "Please select entity" }]}
            >
              <Select
                placeholder="Select entity"
                showSearch
                optionFilterProp="children"
                className="rounded-xl"
              >
                {bankEntities.map((entity) => (
                  <Option key={entity._id} value={entity._id}>
                    {entity.entityName}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              name="title"
              label={<span className={labelCls}>Title</span>}
              rules={[{ required: true, message: "Title is required" }]}
            >
              <Input className="rounded-xl" />
            </Form.Item>

            <Form.Item
              name="description"
              label={<span className={labelCls}>Description</span>}
            >
              <Input.TextArea rows={3} className="rounded-xl" />
            </Form.Item>

            <div className="grid grid-cols-2 gap-x-4">
              <Form.Item
                name="amount"
                label={<span className={labelCls}>Amount</span>}
                rules={[{ required: true, message: "Amount is required" }]}
              >
                <InputNumber className="w-full rounded-xl" min={0} />
              </Form.Item>

              <Form.Item
                name="percentage"
                label={<span className={labelCls}>Percentage (%)</span>}
                rules={[{ required: true, message: "Percentage is required" }]}
              >
                <InputNumber className="w-full rounded-xl" min={0} max={100} />
              </Form.Item>
            </div>

            <Form.Item
              name="date"
              label={<span className={labelCls}>Date</span>}
              rules={[{ required: true, message: "Date is required" }]}
            >
              <DatePicker className="w-full rounded-xl" />
            </Form.Item>

            {mode !== "view" && (
              <button
                type="submit"
                disabled={loading}
                className="w-full mt-2 py-2.5 btn-gradient"
              >
                {loading
                  ? "Saving..."
                  : mode === "add"
                    ? "Create Investment"
                    : "Update Investment"}
              </button>
            )}
          </Form>
        </div>
      )}
    </Drawer>
  );
};

export default InvestmentDetailsDrawer;
